import "./style.css";

type Tour = {
  id: number;
  name: string;
  img: string;
  description: string;
  longText: string;
  mainStops: string[];
  needToBring: string[];
  provided: string[];
};

type FieldGroup = {
  title: string;
  name: string;
  email: string;
  number: string;
  text: string;
  date?: string;
};

type FormFieldsResponse = {
  booking: FieldGroup;
  general: FieldGroup;
};

type ReviewsResponse = {
  reviews: string[];
};

type ToursResponse = {
  tours: Tour[];
};

type FleetItem = {
  id: number;
  name: string;
  img: string;
  description: string;
};

type FleetApiItem = {
  id?: number;
  name?: string;
  title?: string;
  img?: string;
  image?: string;
  description?: string;
  text?: string;
};

type FleetResponse = {
  fleet: FleetApiItem[];
};

const HERO_LOW =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/img-hero-low.webp";
const HERO_HIGH =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/img-hero-high.webp";
const TOURS_URL =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/tours.json";
const REVIEWS_URL =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/reviews.json";
const FORM_FIELDS_URL =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/formFields.json";
const FLEET_URL =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/fleet.json";
const GALLERY_BASE_URL =
  "https://raw.githubusercontent.com/Curanda/Curanda.github.io/main/roman-gallery";
const GALLERY_IMAGE_COUNT = 14;
const TOUR_MODAL_BG_CLASS = "bg-[#111419]";
const TOUR_MODAL_CARD_BG_CLASS = "bg-[#30394a]";
const SITE_URL = "https://romaniniceland.com";
const DEFAULT_SEO_TITLE = "Roman in Iceland | Private Tours from Reykjavik";
const DEFAULT_SEO_DESCRIPTION =
  "Private tours from Reykjavik with Roman in Iceland. Explore Golden Circle, South Coast, Northern Lights and more with tailor-made experiences.";

const appElement = document.querySelector<HTMLDivElement>("#app");

if (!appElement) {
  throw new Error("#app not found");
}

const upsertMeta = (
  attribute: "name" | "property",
  key: string,
  content: string,
) => {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.append(tag);
  }

  tag.content = content;
};

const upsertCanonical = (href: string) => {
  let canonical = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }

  canonical.href = href;
};

const updateSeoMetadata = (selectedTour: Tour | undefined) => {
  const title = selectedTour
    ? `${selectedTour.name} Tour | Roman in Iceland`
    : DEFAULT_SEO_TITLE;
  const description = selectedTour
    ? selectedTour.description
    : DEFAULT_SEO_DESCRIPTION;
  const image = selectedTour?.img ?? HERO_HIGH;
  const url = selectedTour
    ? `${SITE_URL}${getTourPath(selectedTour)}`
    : `${SITE_URL}/`;

  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:image", image);
  upsertMeta("property", "og:url", url);
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", image);
  upsertCanonical(url);
};

const upsertStructuredData = (tours: Tour[]) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TravelAgency",
        "@id": `${SITE_URL}/#organization`,
        name: "Roman in Iceland",
        url: `${SITE_URL}/`,
        logo: "https://raw.githubusercontent.com/Curanda/Curanda.github.io/1b71963426191db677f109cc04297f9bf1f1527a/img-logo-sm.svg",
        sameAs: ["https://www.facebook.com/romannaislandii"],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: "Roman in Iceland",
        description: DEFAULT_SEO_DESCRIPTION,
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
      {
        "@type": "ItemList",
        "@id": `${SITE_URL}/#tours-list`,
        name: "Roman in Iceland Tours",
        itemListElement: tours.map((tour, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "TouristTrip",
            name: tour.name,
            description: tour.description,
            image: tour.img,
            url: `${SITE_URL}${getTourPath(tour)}`,
            provider: { "@id": `${SITE_URL}/#organization` },
          },
        })),
      },
    ],
  };

  let script = document.head.querySelector<HTMLScriptElement>(
    'script[data-seo-jsonld="true"]',
  );

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-jsonld", "true");
    document.head.append(script);
  }

  script.textContent = JSON.stringify(structuredData);
};

const state: {
  tours: Tour[];
  fleet: FleetItem[];
  reviews: string[];
  formFields: FormFieldsResponse | null;
  loadingTours: boolean;
  loadingFleet: boolean;
  loadingReviews: boolean;
  showGeneralModal: boolean;
  selectedTourId: number | null;
  showMobileMenu: boolean;
} = {
  tours: [],
  fleet: [],
  reviews: [],
  formFields: null,
  loadingTours: true,
  loadingFleet: true,
  loadingReviews: true,
  showGeneralModal: false,
  selectedTourId: null,
  showMobileMenu: false,
};

const renderLoading = () =>
  '<span class="loading loading-spinner loading-lg"></span>';

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replaceAll("æ", "ae")
    .replaceAll("ö", "o")
    .replaceAll("ð", "d")
    .replaceAll("þ", "th")
    .normalize("NFKD")
    .replaceAll(/[\u0300-\u036f]/g, "")
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "")
    .replaceAll(/-{2,}/g, "-");

const getTourPath = (tour: Tour) => `/tours/${slugify(tour.name)}/`;

const renderQueryForm = (
  fields: FieldGroup,
  formName: "general-booking-inquiry" | "tour-availability-inquiry",
  useDarkTheme = false,
  tourName?: string,
) => {
  const dividerClasses = useDarkTheme
    ? "divider font-[Montserrat] text-xl text-white before:border-white after:border-white"
    : "divider font-[Montserrat] text-xl";
  const inputClasses = useDarkTheme
    ? "input input-bordered flex items-center border-2 border-white text-white placeholder:text-white/70 bg-transparent"
    : "input input-bordered flex items-center text-neutral-content border-2 border-[#6C788D]";
  const buttonClasses = useDarkTheme
    ? "btn border-solid border-2 border-white font-[Montserrat] rounded-full p-4 text-center uppercase transition bg-transparent hover:bg-white hover:text-[#363c46] text-white"
    : "btn border-solid border-2 border-[#afafaf] font-[Montserrat] rounded-full p-4 text-center uppercase transition bg-transparent hover:bg-[#ababab] hover:border-[#ababab] text-white";

  const controls = Object.entries(fields)
    .filter(([key]) => key !== "title")
    .map(([key, value]) => {
      if (key === "text") {
        return `<textarea
          name="${key}"
          aria-label="${escapeHtml(value)}"
          required
          class="textarea textarea-bordered min-h-36 max-h-56 overflow-y-auto ${useDarkTheme ? "border-2 border-white text-white placeholder:text-white/70 bg-transparent" : "border-2 border-[#6C788D] text-neutral-content"}"
          placeholder="${escapeHtml(value)}"
        ></textarea>`;
      }

      const inputType = key === "name" ? "text" : key;
      const extraClasses =
        useDarkTheme && key === "date" ? "tour-modal-date-input" : "";

      return `<input
        name="${key}"
        type="${inputType}"
        aria-label="${escapeHtml(value)}"
        ${key === "name" || key === "email" ? "required" : ""}
        class="${inputClasses} ${extraClasses}"
        placeholder="${escapeHtml(value)}"
      />`;
    })
    .join("");

  return `<form
    name="${formName}"
    method="POST"
    action="/"
    netlify
    data-netlify="true"
    netlify-honeypot="bot-field"
    data-netlify-form="true"
    class="flex flex-col gap-6"
  >
    <input type="hidden" name="form-name" value="${formName}" />
    <input type="hidden" name="bot-field" />
    ${tourName ? `<input type="hidden" name="tour_name" value="${escapeHtml(tourName)}" />` : ""}
    <div class="${dividerClasses}">${escapeHtml(fields.title)}</div>
    ${controls}
    <button
      type="submit"
      class="${buttonClasses}"
    >
      <p>Send inquiry</p>
    </button>
  </form>`;
};

const renderInfoItems = (items: string[]) =>
  items.length > 0
    ? items
        .map((item) => `<li><p>&#10003; ${escapeHtml(item)}</p></li>`)
        .join("")
    : "<li><p>Nothing</p></li>";

const sanitizeStringList = (items: string[]) =>
  items.map((item) => item.trim()).filter((item) => item.length > 0);

const normalizeTours = (tours: Tour[]): Tour[] =>
  tours.map((tour) => ({
    ...tour,
    mainStops: sanitizeStringList(tour.mainStops),
    provided: sanitizeStringList(tour.provided),
    needToBring: sanitizeStringList(tour.needToBring),
  }));

const normalizeFleet = (fleet: FleetApiItem[]): FleetItem[] =>
  fleet
    .map((item, index) => ({
      id: item.id ?? index + 1,
      name: item.name?.trim() || item.title?.trim() || "",
      img: item.img?.trim() || item.image?.trim() || "",
      description: item.description?.trim() || item.text?.trim() || "",
    }))
    .filter(
      (item) =>
        item.name.length > 0 &&
        item.img.length > 0 &&
        item.description.length > 0,
    );

const renderTourDetailsModal = (tour: Tour) => {
  if (!state.formFields) {
    return `<dialog class="modal" open><div class="modal-box">${renderLoading()}</div></dialog>`;
  }

  const paragraphs = tour.longText
    .split("\n\n")
    .map((paragraph) => `<p class="py-3">${escapeHtml(paragraph.trim())}</p>`)
    .join("");

  return `<dialog class="modal" open>
    <div class="modal-box w-11/12 max-w-5xl border-2 border-white ${TOUR_MODAL_BG_CLASS} text-white">
      <img class="rounded-t-lg object-cover w-full h-96" src="${tour.img}" alt="${escapeHtml(tour.name)}" loading="lazy" width="1536" height="864" />
      <h3 class="font-bold font-[Italiana] text-3xl mt-4 text-center">${escapeHtml(tour.name)} tour</h3>
      <div class="font-thin font-[Montserrat]">${paragraphs}</div>
      <div class="flex flex-col gap-6 lg:flex-row lg:justify-around">
        <div class="grid grid-flow-row auto-rows-min items-center lg:w-1/2 font-[Montserrat] font-medium">
          <div class="flex flex-col items-center">
            <div class="divider text-white before:border-white after:border-white">Highlights</div>
            <div class="card ${TOUR_MODAL_CARD_BG_CLASS} text-white p-6 border-4 border-white w-full">
              <ul>${renderInfoItems(tour.mainStops)}</ul>
            </div>
          </div>
          <div class="flex flex-col items-center">
            <div class="divider text-white before:border-white after:border-white">We provide</div>
            <div class="card ${TOUR_MODAL_CARD_BG_CLASS} text-white p-6 border-4 border-white w-full">
              <ul>${renderInfoItems(tour.provided)}</ul>
            </div>
          </div>
          ${
            tour.needToBring.length > 0
              ? `<div class="flex flex-col items-center">
                <div class="divider text-white before:border-white after:border-white">Good to bring</div>
                <div class="card ${TOUR_MODAL_CARD_BG_CLASS} text-white p-6 border-4 border-white w-full">
                  <ul>${renderInfoItems(tour.needToBring)}</ul>
                </div>
              </div>`
              : ""
          }
        </div>
        ${renderQueryForm(state.formFields.booking, "tour-availability-inquiry", true, tour.name)}
      </div>
      <div class="modal-action">
        <form method="dialog">
          <button data-action="close-tour-modal" class="btn bg-transparent text-white border-white hover:bg-white hover:text-[#363c46]">Close</button>
        </form>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button data-action="close-tour-modal"></button>
    </form>
  </dialog>`;
};

const renderGeneralModal = () => {
  const content = state.formFields
    ? renderQueryForm(state.formFields.general, "general-booking-inquiry", true)
    : renderLoading();

  return `<dialog class="modal modal-bottom sm:modal-middle" open>
    <div class="modal-box border-2 border-white ${TOUR_MODAL_BG_CLASS} text-white">
      ${content}
      <div class="modal-action">
        <form method="dialog">
          <button data-action="close-general-modal" class="btn bg-transparent text-white border-white hover:bg-white hover:text-[#363c46]">Close</button>
        </form>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button data-action="close-general-modal"></button>
    </form>
  </dialog>`;
};

const renderToursMenuItems = () => {
  if (state.loadingTours) {
    return `<li><span class="px-2 py-1">${renderLoading()}</span></li>`;
  }

  if (state.tours.length === 0) {
    return `<li><span class="px-2 py-1 text-sm">No tours available</span></li>`;
  }

  return state.tours
    .map(
      (tour) =>
        `<li><a href="${getTourPath(tour)}" data-action="open-tour" data-tour-id="${tour.id}">${escapeHtml(tour.name)}</a></li>`,
    )
    .join("");
};

const renderGallerySlides = () => {
  const slides = Array.from({ length: GALLERY_IMAGE_COUNT }, (_, index) => {
    const slideNumber = index + 1;
    const previousSlideNumber =
      slideNumber === 1 ? GALLERY_IMAGE_COUNT : slideNumber - 1;
    const nextSlideNumber =
      slideNumber === GALLERY_IMAGE_COUNT ? 1 : slideNumber + 1;

    return `<div id="slide${slideNumber}" class="carousel-item relative w-full">
      <div data-gallery-image-shell class="relative w-full min-h-[260px] md:min-h-[380px] xl:min-h-[520px]">
        <div data-gallery-skeleton class="absolute inset-0 rounded-2xl bg-slate-700/70 animate-pulse"></div>
        <img
          loading="lazy"
          decoding="async"
          fetchpriority="low"
          src="${GALLERY_BASE_URL}/img-gallery-${slideNumber}.webp"
          width="1600"
          height="900"
          alt="Iceland tour photo ${slideNumber}"
          data-gallery-image="true"
          class="object-contain w-full opacity-0 transition-opacity duration-300"
        />
      </div>
      <div class="absolute flex justify-between transform -translate-y-1/2 left-5 right-5 top-1/2">
        <a href="#slide${previousSlideNumber}" aria-label="Previous gallery photo" class="btn btn-circle bg-[#6C788D] border-[#ababab] hover:bg-[#ababab] hover:text-white hover:border-[#ababab] border-2">❮</a>
        <a href="#slide${nextSlideNumber}" aria-label="Next gallery photo" class="btn btn-circle bg-[#6C788D] border-[#ababab] hover:bg-[#ababab] hover:text-white hover:border-[#ababab] border-2">❯</a>
      </div>
    </div>`;
  });

  return slides.join("");
};

const wireGalleryImageSkeletons = () => {
  const galleryImages = document.querySelectorAll<HTMLImageElement>(
    "img[data-gallery-image='true']",
  );

  galleryImages.forEach((image) => {
    const shell = image.closest<HTMLElement>("[data-gallery-image-shell]");
    const skeleton = shell?.querySelector<HTMLElement>(
      "[data-gallery-skeleton]",
    );

    if (!skeleton) {
      return;
    }

    const revealImage = () => {
      image.classList.remove("opacity-0");
      image.classList.add("opacity-100");
      skeleton.classList.add("hidden");
    };

    if (image.complete) {
      revealImage();
      return;
    }

    image.addEventListener("load", revealImage, { once: true });
    image.addEventListener("error", revealImage, { once: true });
  });
};

const wireFleetImageSkeletons = () => {
  const fleetImages = document.querySelectorAll<HTMLImageElement>(
    "img[data-fleet-image='true']",
  );

  fleetImages.forEach((image) => {
    const shell = image.closest<HTMLElement>("[data-fleet-image-shell]");
    const skeleton = shell?.querySelector<HTMLElement>("[data-fleet-skeleton]");

    if (!skeleton) {
      return;
    }

    const revealImage = () => {
      image.classList.remove("opacity-0");
      image.classList.add("opacity-100");
      skeleton.classList.add("hidden");
    };

    if (image.complete) {
      revealImage();
      return;
    }

    image.addEventListener("load", revealImage, { once: true });
    image.addEventListener("error", revealImage, { once: true });
  });
};

const renderTourCards = () => {
  if (state.loadingTours) {
    return `<div class="flex justify-center py-20">${renderLoading()}</div>`;
  }

  const cards = state.tours
    .map(
      (tour) => `<div class="carousel-item max-w-screen-sm">
        <div class="card w-72 md:w-96 bg-neutral shadow-xl transition duration-500 ease-in-out transform hover:scale-105 hover:shadow-lg">
          <figure>
            <img class="rounded-t-lg w-full h-60 object-cover object-center" loading="lazy" src="${tour.img}" alt="${escapeHtml(tour.name)}" width="384" height="240" />
          </figure>
          <div class="card-body">
            <h2 class="card-title mb-4 text-3xl font-[Italiana] tracking-widest">${escapeHtml(tour.name)}</h2>
            <p class="text-[#dcdcdc] font-[Montserrat]">${escapeHtml(tour.description)}</p>
            <div class="card-actions justify-start">
              <a
                href="${getTourPath(tour)}"
                data-action="open-tour"
                data-tour-id="${tour.id}"
                class="btn border-solid border-2 border-[#afafaf] font-[Montserrat] rounded-full p-4 text-center uppercase transition bg-transparent hover:bg-[#ababab] hover:border-[#ababab] text-white"
              >
                Check availability
              </a>
            </div>
          </div>
        </div>
      </div>`,
    )
    .join("");

  return `<div class="carousel max-[780px]:carousel-vertical rounded-box max-w-screen-sm md:max-w-lg lg:max-w-2xl xl:max-w-7xl gap-8 px-4">${cards}</div>`;
};

const renderFleetCards = () => {
  if (state.loadingFleet) {
    return `<div class="flex justify-center py-20">${renderLoading()}</div>`;
  }

  if (state.fleet.length === 0) {
    return `<p class="font-[Montserrat] text-center text-slate-300">Fleet details are coming soon.</p>`;
  }

  const cards = state.fleet
    .map(
      (item) => `<div class="carousel-item max-w-screen-sm">
        <div class="card w-72 md:w-96 bg-neutral shadow-xl transition duration-500 ease-in-out transform hover:scale-105 hover:shadow-lg">
          <figure>
            <div data-fleet-image-shell class="relative w-full h-60">
              <div data-fleet-skeleton class="absolute inset-0 rounded-t-lg bg-slate-700/70 animate-pulse"></div>
              <img
                class="rounded-t-lg w-full h-60 object-cover object-center opacity-0 transition-opacity duration-300"
                loading="lazy"
                decoding="async"
                fetchpriority="low"
                src="${item.img}"
                width="384"
                height="240"
                alt="${escapeHtml(item.name)}"
                data-fleet-image="true"
              />
            </div>
          </figure>
          <div class="card-body">
            <h2 class="card-title mb-4 text-3xl font-[Italiana] tracking-widest">${escapeHtml(item.name)}</h2>
            <p class="text-[#dcdcdc] font-[Montserrat]">${escapeHtml(item.description)}</p>
          </div>
        </div>
      </div>`,
    )
    .join("");

  return `<div class="carousel max-[780px]:carousel-vertical rounded-box max-w-screen-sm md:max-w-lg lg:max-w-2xl xl:max-w-7xl gap-8 px-4">${cards}</div>`;
};

const renderReviews = () => {
  if (state.loadingReviews) {
    return renderLoading();
  }

  return state.reviews
    .map(
      (reviewUrl, index) =>
        `<div class="carousel-item">
          <div data-review-image-shell class="relative">
            <div data-review-skeleton class="absolute inset-0 rounded-box bg-slate-700/70 animate-pulse"></div>
            <img
              src="${reviewUrl}"
              class="rounded-box block h-auto w-auto max-w-full object-contain opacity-0 transition-opacity duration-300"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              width="412"
              height="234"
              alt="Guest review ${index + 1}"
              data-review-image="true"
            />
          </div>
        </div>`,
    )
    .join("");
};

const wireReviewImageSkeletons = () => {
  const reviewImages = document.querySelectorAll<HTMLImageElement>(
    "img[data-review-image='true']",
  );

  reviewImages.forEach((image) => {
    const shell = image.closest<HTMLElement>("[data-review-image-shell]");
    const skeleton = shell?.querySelector<HTMLElement>(
      "[data-review-skeleton]",
    );

    if (!skeleton) {
      return;
    }

    const revealImage = () => {
      image.classList.remove("opacity-0");
      image.classList.add("opacity-100");
      skeleton.classList.add("hidden");
    };

    if (image.complete) {
      revealImage();
      return;
    }

    image.addEventListener("load", revealImage, { once: true });
    image.addEventListener("error", revealImage, { once: true });
  });
};

const handleGalleryNavClick = (target: Element, event: Event) => {
  const galleryLink = target.closest<HTMLAnchorElement>(
    "a[href^='#slide'][aria-label*='gallery photo']",
  );

  if (!galleryLink) {
    return false;
  }

  const targetId = galleryLink.getAttribute("href")?.slice(1);

  if (!targetId) {
    return true;
  }

  const slideElement = document.getElementById(targetId);

  if (!slideElement) {
    return true;
  }

  event.preventDefault();
  slideElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
  return true;
};

const render = () => {
  const selectedTour = state.tours.find(
    (tour) => tour.id === state.selectedTourId,
  );

  updateSeoMetadata(selectedTour);
  upsertStructuredData(state.tours);

  appElement.innerHTML = `
    <main class="text-white">
      <div
        class="relative z-10 w-screen h-screen before:bg-gradient-to-b before:from-transparent before:via-transparent before:to-black before:absolute before:inset-0 before:z-[-1] before:content-[''] before:opacity-80"
      >
        <img
          src="${HERO_LOW}"
          srcset="${HERO_LOW} 1x, ${HERO_HIGH} 2x"
          loading="eager"
          decoding="async"
          fetchpriority="high"
          width="1920"
          height="1080"
          alt="Icelandic landscape"
          class="absolute inset-0 h-full w-full object-cover -z-10"
        />
        <nav aria-label="Primary">
          <div class="navbar fixed top-0 z-10 backdrop-blur-3xl flex-no-wrap flex w-full items-center justify-between shadow-2xl shadow-[#e3e4ed5e] border-b-2 border-[#ffffff2e]">
            <div class="navbar-start">
              <button data-action="toggle-mobile-menu" data-mobile-menu-toggle="true" class="btn btn-ghost lg:hidden" aria-label="Open menu">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h8m-8 6h16" />
                </svg>
              </button>
              <button class="btn btn-ghost text-lg sm:text-2xl" data-action="scroll-top">
                Roman in Iceland private tours
              </button>
            </div>
            <div class="navbar-center hidden lg:flex">
              <ul class="menu menu-horizontal px-1 text-lg">
                <li><a href="#fleet">Fleet</a></li>
                <li>
                  <details>
                    <summary>Tours</summary>
                    <ul class="p-2 bg-base-100 text-black rounded-box min-w-64">${renderToursMenuItems()}</ul>
                  </details>
                </li>
                <li><a href="#gallery">Gallery</a></li>
                <li><a href="#about">About</a></li>
              </ul>
            </div>
            <div class="navbar-end">
              <button
                class="btn text-white text-lg bg-[#363c46] invisible sm:visible"
                data-action="open-general-modal"
              >
                Booking inquiry
              </button>
            </div>
          </div>
        </nav>
        ${
          state.showMobileMenu
            ? `<div class="fixed top-16 left-3 z-[40] lg:hidden" data-mobile-menu-panel="true">
                <div class="w-72 rounded-xl border border-slate-300 bg-white p-2 shadow-xl text-slate-900" style="background-color:#ffffff;color:#0f172a;">
                  <p class="px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-700">Tours</p>
                  <ul class="mb-2 space-y-1 rounded-lg border border-slate-200 bg-white p-2 text-slate-900">${renderToursMenuItems()}</ul>
                  <a class="block rounded-lg px-3 py-2 text-slate-900 hover:bg-slate-100" href="#gallery">Gallery</a>
                  <a class="block rounded-lg px-3 py-2 text-slate-900 hover:bg-slate-100" href="#fleet">Fleet</a>
                  <a class="block rounded-lg px-3 py-2 text-slate-900 hover:bg-slate-100" href="#about">About</a>
                  <button class="mt-1 block w-full rounded-lg px-3 py-2 text-left text-slate-900 hover:bg-slate-100" data-action="open-general-modal">Booking inquiry</button>
                </div>
              </div>`
            : ""
        }

        <div class="md:px-4">
          <h1 class="font-[Italiana] font-extrabold opacity-85 text-center underline decoration-stone-300 text-4xl pt-24 pb-8 md:pb-8 lg:text-7xl lg:pt-72 lg:pb-52">
            We Make the magic happen
          </h1>

          <div class="flex flex-col justify-center items-center mx-8 h-full gap-12 md:flex-col md:justify-around md:items-center md:gap-20 lg:flex-row xl:flex-row xl:justify-around xl:items-center">
            <div class="rounded-2xl max-w-96 bg-[#dcdcdc]/85 text-black shadow-2xl shadow-black border-[#000000]/50 transition duration-500 ease-in-out transform hover:scale-105 hover:shadow-xl">
              <div class="p-5 sm:p-8">
                <div class="flex flex-row gap-5">
                  <img
                    alt="Roman in Iceland logo"
                    width="64"
                    height="64"
                    class="h-16"
                    src="https://raw.githubusercontent.com/Curanda/Curanda.github.io/1b71963426191db677f109cc04297f9bf1f1527a/img-logo-sm.svg"
                  />
                  <h2 class="card-title font-[Montserrat] font-bold text-sm sm:text-2xl mb-3 text-left">
                    Private Tours from Reykjavik
                  </h2>
                </div>
                <p class="font-[Montserrat] font-medium text-xs sm:text-base">
                  Bespoke. Exciting. Breathtaking. Iceland is otherworldly and I'd love to show you the best it's got to offer. Let me know and I'll tailor the tour to your dreams of perfect Iceland experience.
                </p>
                <div class="card-actions justify-end pt-3">
                  <button
                    data-action="scroll-tours"
                    class="text-black font-bold text-sm sm:text-base shadow-2xl outline outline-[#28323d] outline-3 -outline-offset-2 font-[Montserrat] rounded-full p-3 sm:p-4 text-center uppercase transition ease-in-out bg-transparent hover:bg-[#28323d] hover:text-[#dcdcdc]"
                  >
                    BOOK HERE
                  </button>
                </div>
              </div>
            </div>

            <div class="transition duration-500 ease-in-out transform hover:scale-105 hover:shadow-2xl shadow-black rounded-3xl">
              <h2 class="text-center font-[Montserrat] text-2xl">Our reviews</h2>
              <div class="carousel carousel-center max-w-[420px] p-4 space-x-1 rounded-3xl bg-[#000000]/50 opacity-80">
                ${renderReviews()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <section id="tours" class="section defer-render scroll-mt-24 bg-black text-white flex flex-col items-center max-w-screen px-4 overflow-clip py-16">
      <h2 class="font-[Italiana] text-white text-center pb-6 text-3xl md:pb-8 lg:pb-20 lg:text-6xl bg-black">
        Let us take you for a tour
      </h2>
      ${renderTourCards()}
    </section>

    <section id="gallery" class="section defer-render scroll-mt-24 bg-black px-8 py-20 md:p-32 flex flex-col items-center gap-10 md:gap-20">
      <h2 class="font-[Italiana] text-white text-3xl md:text-5xl text-center">Photo gallery</h2>
      <div class="carousel rounded-2xl xl:size-4/5">
        ${renderGallerySlides()}
      </div>
    </section>

    <section id="fleet" class="section defer-render scroll-mt-24 bg-black text-white flex flex-col items-center max-w-screen px-4 overflow-clip py-16">
      <h2 class="font-[Italiana] text-white text-center pb-6 text-3xl md:pb-8 lg:pb-20 lg:text-6xl bg-black">
        Meet Our Fleet
      </h2>
      ${renderFleetCards()}
    </section>

    <section id="about" class="section defer-render scroll-mt-24 bg-black text-white px-6 pb-20 md:px-20 lg:px-32">
      <div class="mx-auto max-w-5xl rounded-2xl border-2 border-[#ffffff2e] bg-neutral p-8 md:p-12">
        <h2 class="font-[Italiana] text-3xl md:text-5xl text-center mb-6">About Me</h2>
        <div class="font-[Montserrat] text-[#dcdcdc] text-base md:text-lg leading-relaxed space-y-4">
          <p>
            I am Roman, your private guide in Iceland. My goal is to create memorable journeys that feel personal,
            relaxed, and fully tailored to your interests.
          </p>
          <p>
            Whether you want famous landmarks, quiet hidden spots, photography stops, or flexible pacing, I plan each
            day around your travel style so you can experience Iceland at its best.
          </p>
          <p>
            My focus includes not only regular tours but also Icelandic highlands. I'm also a passionate northern lights hunter - I know all the good spots and all the techniques necessary to facilitate a successfull northern lights observation.
          </p>

          <h3 class="text-xl font-bold mt-6">Company information</h3>
          <p class="text-white">Roman in Iceland ehf.</p>
          <p class="text-white">kt. 500524-0490</p>
          <p class="text-blue-400">phone: (+354) 770 7213</p>
        </div>
      </div>
    </section>

    <footer class="footer p-10 bg-neutral text-neutral-content">
      <aside>
        <img src="https://raw.githubusercontent.com/Curanda/Curanda.github.io/1b71963426191db677f109cc04297f9bf1f1527a/img-logo-sm.svg" alt="Roman in Iceland logo" width="80" height="80" class="w-20 h-20" />
        <p>&copy;${new Date().getFullYear()} logo and website design by Nordic Gentleman ehf. All rights reserved.</p>
      </aside>
      <nav>
        <h3 class="footer-title">Social media</h3>
        <div class="grid grid-flow-col gap-4">
          <a href="https://www.instagram.com/romanedwardson/?hl=pl" target="_blank" rel="noreferrer" aria-label="Roman in Iceland on Instagram">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="fill-current">
              <path d="M7.75 2h8.5A5.76 5.76 0 0 1 22 7.75v8.5A5.76 5.76 0 0 1 16.25 22h-8.5A5.76 5.76 0 0 1 2 16.25v-8.5A5.76 5.76 0 0 1 7.75 2Zm-.2 2A3.55 3.55 0 0 0 4 7.55v8.9A3.55 3.55 0 0 0 7.55 20h8.9A3.55 3.55 0 0 0 20 16.45v-8.9A3.55 3.55 0 0 0 16.45 4h-8.9Zm9.95 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"></path>
            </svg>
          </a>
          <a href="https://www.facebook.com/romannaislandii" target="_blank" rel="noreferrer" aria-label="Roman in Iceland on Facebook">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="fill-current">
              <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path>
            </svg>
          </a>
        </div>
      </nav>
    </footer>

    ${state.showGeneralModal ? renderGeneralModal() : ""}
    ${selectedTour ? renderTourDetailsModal(selectedTour) : ""}
  `;

  wireGalleryImageSkeletons();
  wireFleetImageSkeletons();
  wireReviewImageSkeletons();
};

const handleClick = (event: Event) => {
  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  if (handleGalleryNavClick(target, event)) {
    return;
  }

  const clickedInsideMobileMenu = Boolean(
    target.closest("[data-mobile-menu-panel]"),
  );
  const clickedMobileMenuToggle = Boolean(
    target.closest("[data-mobile-menu-toggle]"),
  );

  if (
    state.showMobileMenu &&
    !clickedInsideMobileMenu &&
    !clickedMobileMenuToggle
  ) {
    state.showMobileMenu = false;
    render();
    return;
  }

  const actionElement = target.closest<HTMLElement>("[data-action]");

  if (!actionElement) {
    return;
  }

  const action = actionElement.dataset.action;

  if (!action) {
    return;
  }

  switch (action) {
    case "scroll-top": {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (window.location.pathname !== "/" || window.location.hash.length > 0) {
        window.history.pushState({}, "", "/");
      }
      state.showMobileMenu = false;
      break;
    }
    case "scroll-tours": {
      document
        .querySelector<HTMLElement>("#tours")
        ?.scrollIntoView({ behavior: "smooth" });
      state.showMobileMenu = false;
      break;
    }
    case "toggle-mobile-menu": {
      state.showMobileMenu = !state.showMobileMenu;
      break;
    }
    case "open-general-modal": {
      state.showGeneralModal = true;
      state.showMobileMenu = false;
      break;
    }
    case "close-general-modal": {
      state.showGeneralModal = false;
      break;
    }
    case "open-tour": {
      event.preventDefault();
      const id = Number(actionElement.dataset.tourId);
      if (!Number.isNaN(id)) {
        state.selectedTourId = id;
        state.showMobileMenu = false;
      }
      break;
    }
    case "close-tour-modal": {
      state.selectedTourId = null;
      break;
    }
    default:
      return;
  }

  render();
};

const handleFormSubmit = async (event: Event) => {
  const target = event.target;

  if (!(target instanceof HTMLFormElement)) {
    return;
  }

  if (target.dataset.netlifyForm !== "true") {
    return;
  }

  event.preventDefault();

  const toUrlEncoded = (payload: FormData) => {
    const encoded = new URLSearchParams();

    payload.forEach((value, key) => {
      encoded.append(key, typeof value === "string" ? value : value.name);
    });

    return encoded.toString();
  };

  const formData = new FormData(target);

  try {
    const response = await fetch("/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: toUrlEncoded(formData),
    });

    if (!response.ok) {
      throw new Error(`Form submit failed with status ${response.status}`);
    }

    const submittedFormName = String(formData.get("form-name") ?? "");

    if (submittedFormName === "tour-availability-inquiry") {
      state.selectedTourId = null;
    }

    if (submittedFormName === "general-booking-inquiry") {
      state.showGeneralModal = false;
    }

    target.reset();
    window.alert("Thank you! Your inquiry has been sent.");
    render();
  } catch (error) {
    console.error(error);
    window.alert(
      "Sorry, there was a problem sending your inquiry. Please try again.",
    );
  }
};

document.addEventListener("click", handleClick);
document.addEventListener("submit", (event) => {
  void handleFormSubmit(event);
});

const fetchData = async () => {
  try {
    const fleetPromise = fetch(FLEET_URL, { headers: { Accept: "*/*" } })
      .then((response) => response.json() as Promise<FleetResponse>)
      .catch(() => ({ fleet: [] }) as FleetResponse);

    const [toursResponse, reviewsResponse, fieldsResponse, fleetData] =
      await Promise.all([
        fetch(TOURS_URL, { headers: { Accept: "*/*" } }),
        fetch(REVIEWS_URL, { headers: { Accept: "*/*" } }),
        fetch(FORM_FIELDS_URL, { headers: { Accept: "*/*" } }),
        fleetPromise,
      ]);

    const toursData = (await toursResponse.json()) as ToursResponse;
    const reviewsData = (await reviewsResponse.json()) as ReviewsResponse;
    const fieldsData = (await fieldsResponse.json()) as FormFieldsResponse;

    state.tours = normalizeTours(toursData.tours);
    state.fleet = normalizeFleet(fleetData.fleet ?? []);
    state.reviews = reviewsData.reviews;
    state.formFields = fieldsData;
  } catch (error) {
    console.error(error);
  } finally {
    state.loadingTours = false;
    state.loadingFleet = false;
    state.loadingReviews = false;
    render();
  }
};

render();
void fetchData();
